(() => {
  if (customElements.get('suri-buy-now')) return;
  let active = false;

  customElements.define('suri-buy-now', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.ready) return;
        this.ready = true;
        this.button = this.querySelector('button');
        this.error = this.querySelector('[role="alert"]');
        this.form = this.parentElement.querySelector('product-form form');
        if (!this.button || !this.form) return;
        this.label = this.button.textContent;
        this.scope = this.closest('.product__info-container') || this.parentElement;
        this.optionsHandler = () => {
          if (this.optionsError && !this.pending) {
            this.error.hidden = this.missingOptions().length === 0;
          }
        };
        this.scope.addEventListener('input', this.optionsHandler);
        this.scope.addEventListener('change', this.optionsHandler);
        this.clickHandler = () => this.checkout();
        this.button.addEventListener('click', this.clickHandler);
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(this.form, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        this.sync();
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.clickHandler);
      this.scope?.removeEventListener('input', this.optionsHandler);
      this.scope?.removeEventListener('change', this.optionsHandler);
      this.ready = false;
    }

    submitButton() {
      return this.form.querySelector('.gpo-clone-atc-button') || this.form.querySelector('[name="add"]');
    }

    sync() {
      this.button.disabled = false;
      this.button.setAttribute('aria-busy', String(Boolean(this.pending)));
    }

    missingOptions() {
      return Array.from(this.scope.querySelectorAll('.gpo-element')).filter(group => {
        if (!group.getClientRects().length) return false;
        const required = Array.from(group.querySelectorAll('input[presence], select[presence], textarea[presence]'))
          .filter(input => !input.disabled);
        return required.some(input => {
          if (input.type === 'radio' || input.type === 'checkbox') {
            return !required.some(option => option.name === input.name && option.checked);
          }
          if (input.type === 'file') {
            const saved = Array.from(this.form.querySelectorAll('.gpo-hidden-inputs [data-field-name]'))
              .some(field => !field.disabled && field.dataset.fieldName === input.dataset.fieldName && field.value);
            return !input.files?.length && !input.hasAttribute('data-has-file') && !saved;
          }
          if (input.multiple && input.options) {
            return !Array.from(input.selectedOptions).some(option => !option.disabled && option.value);
          }
          return !input.value.trim();
        });
      });
    }

    showOptionsError() {
      this.optionsError = true;
      this.error.textContent = this.dataset.options;
      this.error.hidden = false;
    }

    async quantityInCart(variantId) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(this.dataset.unconfirmed);
      const cart = await response.json();
      return cart.items.reduce((sum, item) => sum + (String(item.variant_id || item.id) === variantId ? item.quantity : 0), 0);
    }

    confirmAdd(submit, variantId, quantity, quantityBefore, usesGlobo) {
      return new Promise((resolve, reject) => {
        let settled = false;
        let verifying = false;
        const finish = error => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          document.removeEventListener('variant:add', onAdded);
          document.removeEventListener('cart:error', onError);
          document.removeEventListener('gpoAddedToCart', onGloboAdded);
          document.removeEventListener('invalid', onInvalid, true);
          validationObserver.disconnect();
          if (error) reject(error); else resolve();
        };
        const onAdded = event => {
          const detail = event.detail || {};
          if (detail.formElement === this.form && String(detail.variant?.id) === variantId && !usesGlobo) finish();
        };
        const onError = event => {
          const detail = event.detail || {};
          if (String(detail.productVariantId) === variantId) finish(new Error(detail.errors || detail.message || this.dataset.unconfirmed));
        };
        const onInvalid = event => {
          const scope = this.closest('.product__info-container');
          if (event.target.form === this.form || (scope?.contains(event.target) && event.target.matches('.gpo-input'))) {
            finish(new Error(this.dataset.options));
          }
        };
        const onGloboAdded = async () => {
          if (!usesGlobo || verifying || settled) return;
          verifying = true;
          try {
            if (await this.quantityInCart(variantId) >= quantityBefore + quantity) finish();
          } catch (error) {
            finish(error);
          } finally {
            verifying = false;
          }
        };
        const checkValidation = () => {
          if (this.scope.querySelector('.gpo-element.has-error .messages .error')) {
            finish(new Error(this.dataset.options));
          }
        };
        const validationObserver = new MutationObserver(checkValidation);
        validationObserver.observe(this.scope, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
        document.addEventListener('variant:add', onAdded);
        document.addEventListener('cart:error', onError);
        document.addEventListener('gpoAddedToCart', onGloboAdded);
        document.addEventListener('invalid', onInvalid, true);
        const timer = setTimeout(() => finish(new Error(this.dataset.unconfirmed)), 15000);
        try { submit.click(); checkValidation(); } catch (error) { finish(error); }
      });
    }

    async checkout() {
      if (active || this.pending) return;
      const submit = this.submitButton();
      const variant = this.form.querySelector('[name="id"]');
      this.error.hidden = true;
      this.optionsError = false;
      if (this.missingOptions().length) {
        this.showOptionsError();
        return;
      }
      if (!submit || !variant?.value || submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
        this.error.textContent = this.dataset.unavailable;
        this.error.hidden = false;
        return;
      }
      if (!this.form.reportValidity()) {
        this.showOptionsError();
        return;
      }
      const usesGlobo = submit.classList.contains('gpo-clone-atc-button');
      const variantId = variant.value;
      const quantity = Number(new FormData(this.form).get('quantity') || 1);
      if (!Number.isInteger(quantity) || quantity < 1) return;
      active = true;
      this.pending = true;
      this.sync();
      this.button.textContent = this.dataset.loading;
      try {
        const quantityBefore = usesGlobo ? await this.quantityInCart(variantId) : 0;
        if (this.missingOptions().length) throw new Error(this.dataset.options);
        if (variant.value !== variantId || Number(new FormData(this.form).get('quantity') || 1) !== quantity || submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
          throw new Error(this.dataset.options);
        }
        await this.confirmAdd(submit, variantId, quantity, quantityBefore, usesGlobo);
        const root = window.Shopify?.routes?.root || '/';
        window.location.assign(root + 'checkout');
      } catch (error) {
        this.optionsError = error.message === this.dataset.options;
        this.error.textContent = error.message || this.dataset.unconfirmed;
        this.error.hidden = false;
      } finally {
        active = false;
        this.pending = false;
        this.button.textContent = this.label;
        this.sync();
      }
    }
  });
})();

